/**
 * Thin typed wrapper around @google/genai.
 *
 * All Gemini SDK access in the app routes through this module. Feature code
 * should never import the SDK directly — see the no-restricted-imports rule in
 * eslint.config.js.
 *
 * The wrapper unifies:
 *   - Client construction (httpOptions.baseUrl override to prevent extension
 *     hijacking; was duplicated as createGeminiClient in src/utils/).
 *   - Text completion + JSON-mode (responseMimeType + responseSchema).
 *   - Token-usage extraction (one source for input/output counts).
 *   - Type re-export so schema authors don't need to import from the SDK.
 *
 * If the SDK ships a breaking change, this is the only file that needs to know.
 */

import { GoogleGenAI, Type, HarmCategory, HarmBlockThreshold, type SafetySetting } from '@google/genai';

// Re-export so schema/safety authors can avoid direct SDK imports.
export { Type, HarmCategory, HarmBlockThreshold };
export type { SafetySetting };

const GEMINI_BASE_URL = 'https://generativelanguage.googleapis.com/';

export function createGeminiClient(apiKey: string): GoogleGenAI {
  return new GoogleGenAI({
    apiKey,
    httpOptions: { baseUrl: GEMINI_BASE_URL },
  });
}

interface GenerateBase {
  apiKey: string;
  model: string;
  /** A single user prompt string — wrapped in the SDK's contents[parts] shape internally. */
  prompt: string;
  systemInstruction?: string;
  temperature?: number;
  /** Nucleus-sampling cutoff. */
  topP?: number;
  /** Hard cap on tokens emitted by the model (SDK config.maxOutputTokens). */
  maxOutputTokens?: number;
  /** Optional abort signal for cancellation. */
  signal?: AbortSignal;
}

export interface GeminiTextRequest extends GenerateBase {
  /**
   * Optional structured-output schema. When set, the SDK is told to emit
   * JSON conforming to the schema; the returned `text` is the JSON string
   * (caller parses — useful when the caller needs custom repair logic).
   * For auto-parsed results, use `generateJson` instead.
   * Implies `responseMimeType: 'application/json'` automatically.
   */
  responseSchema?: unknown;
  /**
   * Override the response MIME type. Useful for JSON mode without a schema
   * (system prompt dictates structure). Setting `responseSchema` already
   * implies `application/json` — don't set both.
   */
  responseMimeType?: string;
  /**
   * Per-call safety setting overrides. Pass-through to the SDK config.
   * Only relevant for image-prompt / creative-bypass use cases.
   */
  safetySettings?: SafetySetting[];
}

export interface GeminiTextResponse {
  text: string;
  inputTokens: number;
  outputTokens: number;
  model: string;
}

export interface GeminiJsonRequest<TSchema = unknown> extends GenerateBase {
  /** A Gemini responseSchema built with Type.OBJECT / Type.STRING / Type.ARRAY. */
  schema: TSchema;
}

export interface GeminiJsonResponse<T> {
  data: T;
  /** Raw JSON text in case the caller needs to inspect formatting. */
  text: string;
  inputTokens: number;
  outputTokens: number;
  model: string;
}

function extractUsage(meta: { promptTokenCount?: number; candidatesTokenCount?: number } | undefined) {
  return {
    inputTokens: meta?.promptTokenCount ?? 0,
    outputTokens: meta?.candidatesTokenCount ?? 0,
  };
}

export async function generateText(req: GeminiTextRequest): Promise<GeminiTextResponse> {
  const ai = createGeminiClient(req.apiKey);
  const response = await ai.models.generateContent({
    model: req.model,
    config: {
      ...(req.systemInstruction ? { systemInstruction: req.systemInstruction } : {}),
      ...(req.temperature !== undefined ? { temperature: req.temperature } : {}),
      ...(req.topP !== undefined ? { topP: req.topP } : {}),
      ...(req.maxOutputTokens !== undefined ? { maxOutputTokens: req.maxOutputTokens } : {}),
      ...(req.signal ? { abortSignal: req.signal } : {}),
      ...(req.responseSchema !== undefined
        ? { responseMimeType: 'application/json', responseSchema: req.responseSchema }
        : req.responseMimeType
          ? { responseMimeType: req.responseMimeType }
          : {}),
      ...(req.safetySettings !== undefined ? { safetySettings: req.safetySettings } : {}),
    },
    contents: [{ parts: [{ text: req.prompt }] }],
  });
  const usage = extractUsage(response.usageMetadata);
  return {
    text: response.text ?? '',
    inputTokens: usage.inputTokens,
    outputTokens: usage.outputTokens,
    model: req.model,
  };
}

export async function generateJson<T>(req: GeminiJsonRequest): Promise<GeminiJsonResponse<T>> {
  const ai = createGeminiClient(req.apiKey);
  const response = await ai.models.generateContent({
    model: req.model,
    config: {
      ...(req.systemInstruction ? { systemInstruction: req.systemInstruction } : {}),
      ...(req.temperature !== undefined ? { temperature: req.temperature } : {}),
      ...(req.maxOutputTokens !== undefined ? { maxOutputTokens: req.maxOutputTokens } : {}),
      ...(req.signal ? { abortSignal: req.signal } : {}),
      responseMimeType: 'application/json',
      responseSchema: req.schema,
    },
    contents: [{ parts: [{ text: req.prompt }] }],
  });
  const usage = extractUsage(response.usageMetadata);
  const text = response.text ?? '';
  // Gemini's JSON mode returns the body as a JSON string; the caller wants
  // the parsed object. Throw on parse failure so the bug surfaces here rather
  // than producing a confusing `undefined` downstream.
  const data = JSON.parse(text) as T;
  return {
    data,
    text,
    inputTokens: usage.inputTokens,
    outputTokens: usage.outputTokens,
    model: req.model,
  };
}
